import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout.jsx';
import { api } from '../../api/client.js';
import { useSettings } from '../../context/SettingsContext.jsx';
import { MORPH_BTN_SM } from '../../lib/ui.js';
import LoginSuccessModal from '../components/LoginSuccessModal.jsx';

// Port persis dari admin/dashboard.php ($stat_cards array)
const STAT_CARDS = [
  { key: 'carousel', label: 'Carousel', icon: 'photo_library', bg: 'color-mix(in oklab, var(--accent) 10%, transparent)', color: 'var(--accent)', to: '/admin/carousel' },
  { key: 'layanan', label: 'Layanan', icon: 'apps', bg: 'rgba(230,160,0,0.1)', color: '#E6A000', to: '/admin/layanan' },
  { key: 'portfolio', label: 'Portfolio', icon: 'photo_camera', bg: 'rgba(58,106,170,0.1)', color: '#3A6AAA', to: '/admin/portfolio' },
  { key: 'testimonial', label: 'Testimonial', icon: 'format_quote', bg: 'rgba(106,74,154,0.1)', color: '#6A4A9A', to: '/admin/testimonial' },
  { key: 'kontak', label: 'Total Pesan', icon: 'mail', bg: 'rgba(220,80,60,0.1)', color: '#DC503C', to: '/admin/kontak' },
  { key: 'kontak_unread', label: 'Belum Dibaca', icon: 'mark_email_unread', bg: 'rgba(220,80,60,0.1)', color: '#DC503C', to: '/admin/kontak' },
];

function fmtTanggal(dateStr) {
  const d = new Date(dateStr);
  const BULAN = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
  return `${d.getDate()} ${BULAN[d.getMonth()]} ${d.getFullYear()}`;
}

export default function AdminDashboard() {
  const { settings } = useSettings();
  const [stats, setStats] = useState(null);
  const [recentKontak, setRecentKontak] = useState(null);
  const [showLoginSuccess, setShowLoginSuccess] = useState(false);
  const color = settings.app_color || 'var(--accent)';

  useEffect(() => {
    api.get('/admin/dashboard').then(({ data }) => {
      setStats(data.stats);
      setRecentKontak(data.recentKontak || []);
    });
  }, []);

  // Munculkan dialog "Login Berhasil" hanya sekali, tepat setelah user mendarat di dashboard pasca login
  useEffect(() => {
    if (sessionStorage.getItem('epw_just_logged_in') === '1') {
      sessionStorage.removeItem('epw_just_logged_in');
      setShowLoginSuccess(true);
    }
  }, []);

  return (
    <AdminLayout title="Dashboard">
      <LoginSuccessModal open={showLoginSuccess} onClose={() => setShowLoginSuccess(false)} />
      {/* Hero Section Banner — port dari dashboard.php */}
      <div className="relative overflow-hidden rounded-2xl border border-neutral-200/80 dark:border-neutral-800 bg-gradient-to-br from-[var(--accent)]/10 via-neutral-50 to-white dark:from-[var(--accent)]/30 dark:via-[var(--accent)]/20 dark:to-neutral-950 p-6 md:p-8 dark:shadow-2xl transition-all duration-300">
        <div className="absolute -right-10 -top-10 hidden h-40 w-40 rounded-full bg-[var(--accent)]/10 blur-3xl dark:block" />
        <div className="absolute -bottom-10 -left-10 hidden h-40 w-40 rounded-full bg-[var(--accent)]/5 blur-3xl dark:block" />

        <div className="relative z-10 flex flex-col justify-between items-start gap-4 lg:flex-row lg:items-center md:gap-6">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-[var(--accent)]/10 dark:bg-[var(--color-forest-400)]/20 px-3 py-1 text-[11px] md:text-xs font-bold uppercase tracking-wider text-[var(--accent)] dark:text-[var(--color-forest-400)]">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--accent)] animate-pulse" />
              Panel Admin
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-neutral-900 dark:text-white">
              Kelola Website{' '}
              <span className="font-display bg-gradient-to-r from-[var(--accent)] to-[var(--accent)] dark:from-[var(--color-forest-400)] dark:to-[var(--color-forest-400)] bg-clip-text text-transparent">
                {settings.site_name}
              </span>
            </h1>
            <p className="max-w-3xl text-sm md:text-base leading-relaxed text-neutral-600 dark:text-neutral-400">
              Kelola hero carousel, data layanan, galeri portfolio, testimoni pelanggan, respon pesan masuk, serta konfigurasi pengaturan situs dalam satu dashboard modern.
            </p>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="mt-8 mb-8 grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
        {STAT_CARDS.map((sc) => (
          <Link key={sc.key} to={sc.to} className="group relative overflow-hidden rounded-2xl border bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-800 p-5 transition-all duration-300 hover:shadow-lg hover:rounded-3xl active:scale-[0.98]">
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl transition-transform group-hover:scale-110" style={{ background: sc.bg }}>
              <span className="material-symbols-outlined text-base" style={{ color: sc.color }}>{sc.icon}</span>
            </div>
            <div className="text-2xl font-bold text-neutral-900 dark:text-white font-display">{stats?.[sc.key] ?? '—'}</div>
            <div className="mt-0.5 text-xs text-neutral-400">{sc.label}</div>
            <div className="absolute right-0 bottom-0 left-0 h-0.5 origin-left scale-x-0 transition-transform duration-300 group-hover:scale-x-100" style={{ background: sc.color }} />
          </Link>
        ))}
      </div>

      {/* Recent Messages Table */}
      <div className="overflow-hidden rounded-3xl border bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-800">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100 dark:border-neutral-800">
          <h3 className="font-bold text-neutral-900 dark:text-white leading-none">Pesan Terbaru</h3>
          <Link to="/admin/kontak" className={`rounded-lg px-3 py-1.5 text-xs font-semibold flex items-center h-8 ${MORPH_BTN_SM}`} style={{ background: 'color-mix(in oklab, var(--accent) 10%, transparent)', color: 'var(--accent)' }}>
            Lihat Semua <span className="material-symbols-outlined ml-1 text-sm leading-none">arrow_forward</span>
          </Link>
        </div>

        {recentKontak === null ? (
          <div className="py-14 text-center text-neutral-400">Memuat...</div>
        ) : recentKontak.length === 0 ? (
          <div className="py-14 text-center text-neutral-400">
            <span className="material-symbols-outlined mb-3 block text-5xl">inbox</span>
            <p className="text-sm">Belum ada pesan masuk</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead className="bg-neutral-50/75 dark:bg-neutral-800/40 text-neutral-500 dark:text-neutral-400 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-6 py-3.5 text-left font-semibold align-middle leading-none">Nama</th>
                  <th className="hidden md:table-cell px-6 py-3.5 text-left font-semibold align-middle leading-none">Telepon</th>
                  <th className="hidden md:table-cell px-6 py-3.5 text-left font-semibold align-middle leading-none">Paket Layanan</th>
                  <th className="px-6 py-3.5 text-left font-semibold align-middle leading-none">Tanggal</th>
                  <th className="px-6 py-3.5 text-left font-semibold align-middle leading-none">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800/60">
                {recentKontak.map((k) => (
                  <tr key={k.id} className="transition-colors hover:bg-neutral-50/50 dark:hover:bg-neutral-800/30">
                    <td className="px-6 py-3 align-middle">
                      <div className="flex items-center gap-2 h-5">
                        {!k.is_read && <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ background: 'var(--accent)' }} />}
                        <span className={`text-sm text-neutral-900 dark:text-white truncate leading-none ${!k.is_read ? 'font-semibold' : 'font-medium'}`}>{k.nama}</span>
                      </div>
                    </td>
                    <td className="hidden md:table-cell px-6 py-3 text-sm text-neutral-500 align-middle leading-5">{k.telepon || '-'}</td>
                    <td className="hidden md:table-cell px-6 py-3 text-sm align-middle leading-5">
                      {k.paket_layanan ? (
                        <span className="inline-flex items-center rounded-lg px-2 py-0.5 text-xs font-semibold bg-[var(--accent)]/10 text-[var(--accent)] dark:bg-[var(--color-forest-400)]/20 dark:text-[var(--color-forest-400)]">{k.paket_layanan}</span>
                      ) : (
                        <span className="text-neutral-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-xs text-neutral-400 align-middle leading-5">{fmtTanggal(k.created_at)}</td>
                    <td className="px-6 py-3 align-middle text-left">
                      <span
                        className="inline-flex items-center justify-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold h-5"
                        style={k.is_read ? { background: 'color-mix(in oklab, var(--accent) 10%, transparent)', color: 'var(--accent)' } : { background: 'rgba(220,80,60,0.1)', color: '#DC503C' }}
                      >
                        {k.is_read ? 'Dibaca' : 'Baru'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
